/**
 * Pure interest + mutual-match logic (no I/O, no localStorage).
 */

import type {
  ExpressInterestInput,
  ExpressInterestResult,
  Interest,
  MutualMatch,
} from "./interestTypes";
import { pairKey, sortedUserIds } from "./interestTypes";

function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export function hasLiked(
  interests: readonly Interest[],
  fromUserId: string,
  toUserId: string
): boolean {
  return interests.some(
    (i) =>
      i.fromUserId === fromUserId &&
      i.toUserId === toUserId &&
      i.status === "liked"
  );
}

export function hasPassed(
  interests: readonly Interest[],
  fromUserId: string,
  toUserId: string
): boolean {
  return interests.some(
    (i) =>
      i.fromUserId === fromUserId &&
      i.toUserId === toUserId &&
      i.status === "passed"
  );
}

/** True if `fromUserId` already acted (like or pass) toward `toUserId`. */
export function hasActed(
  interests: readonly Interest[],
  fromUserId: string,
  toUserId: string
): boolean {
  return interests.some(
    (i) => i.fromUserId === fromUserId && i.toUserId === toUserId
  );
}

/** Counter-like: the other person already liked this user. */
export function hasReverseLike(
  interests: readonly Interest[],
  fromUserId: string,
  toUserId: string
): boolean {
  return hasLiked(interests, toUserId, fromUserId);
}

export function findActiveMatch(
  matches: readonly MutualMatch[],
  userA: string,
  userB: string
): MutualMatch | null {
  const key = pairKey(userA, userB);
  return (
    matches.find(
      (m) =>
        m.status === "active" && pairKey(m.userIds[0], m.userIds[1]) === key
    ) ?? null
  );
}

/**
 * Upsert semantics for a single interest edge (pure):
 * replaces any prior from→to interest in the returned list.
 */
export function upsertInterestInList(
  interests: readonly Interest[],
  interest: Interest
): Interest[] {
  return [
    ...interests.filter(
      (i) =>
        !(i.fromUserId === interest.fromUserId && i.toUserId === interest.toUserId)
    ),
    interest,
  ];
}

/**
 * Express one-way interest.
 * A MutualMatch is created only when:
 * 1. status === "liked"
 * 2. reverse edge already has status "liked"
 * 3. no active match exists yet
 *
 * "passed" never creates a match.
 */
export function expressInterest(input: ExpressInterestInput): ExpressInterestResult {
  const { fromUserId, toUserId, status } = input;
  if (!fromUserId || !toUserId) {
    throw new Error("fromUserId and toUserId are required");
  }
  if (fromUserId === toUserId) {
    throw new Error("Cannot express interest in yourself");
  }

  const now = (input.now ?? new Date()).toISOString();
  const interest: Interest = {
    id: uid("int"),
    fromUserId,
    toUserId,
    status,
    createdAt: now,
  };

  if (status !== "liked") {
    return { interest, match: null, mutual: false };
  }

  const reverseLiked = hasReverseLike(
    input.existingInterests,
    fromUserId,
    toUserId
  );
  if (!reverseLiked) {
    return { interest, match: null, mutual: false };
  }

  const existing = findActiveMatch(
    input.existingMatches,
    fromUserId,
    toUserId
  );
  if (existing) {
    return { interest, match: existing, mutual: true };
  }

  const match: MutualMatch = {
    id: uid("match"),
    userIds: sortedUserIds(fromUserId, toUserId),
    createdAt: now,
    status: "active",
    valuesScore: input.valuesScore ?? 0,
    chatUnlocked: false,
  };

  return { interest, match, mutual: true };
}

/** IDs this user has liked or passed (for deck exclusion). */
export function actedTargetIds(
  interests: readonly Interest[],
  fromUserId: string
): Set<string> {
  const set = new Set<string>();
  for (const i of interests) {
    if (i.fromUserId === fromUserId) set.add(i.toUserId);
  }
  return set;
}

/** Partner IDs from active mutual matches involving this user. */
export function matchedPartnerIds(
  matches: readonly MutualMatch[],
  userId: string
): Set<string> {
  const set = new Set<string>();
  for (const m of matches) {
    if (m.status !== "active") continue;
    if (m.userIds[0] === userId) set.add(m.userIds[1]);
    else if (m.userIds[1] === userId) set.add(m.userIds[0]);
  }
  return set;
}

/** Other user id in a mutual match relative to viewer. */
export function otherUserId(match: MutualMatch, viewerId: string): string {
  if (match.userIds[0] === viewerId) return match.userIds[1];
  if (match.userIds[1] === viewerId) return match.userIds[0];
  return match.userIds[0];
}
